import React, { useEffect, useRef, useState } from "react";
import { Breadcrumbs, Chip, CircularProgress, IconButton, makeStyles, Typography } from "@material-ui/core";
import Layout from "../../components/layout/layout";
import MarkdownWrapper from "../../components/shared/markdown-wrapper";
import Section from "../../components/shared/section";
import cx from "classnames";
import { navigate } from "@reach/router";
import PrintIcon from "@material-ui/icons/Print";
import { Link } from "gatsby";
// import { isMobile } from "react-device-detect";
import { smoothScrolling, supportsSmoothScrollingFunc } from "./smooth-scrolling";

const useStyles = makeStyles((theme) => ({
  markdownWrapperContainer: {
    '& ul': {
      listStylePosition: 'inside',
    },
  },
  mainWithoutNav: {
    flexDirection: "row",
    position: "relative",
    maxWidth: 960,
  },
  main: {
    maxWidth: 960,
    width: "100%",
    display: "flex",

    [theme.breakpoints.down(1300)]: {
      justifyContent: "flex-start",

    },
    [theme.breakpoints.down(940)]: {
      width: "100%",
    },
  },
  mainSection: {
    flex: "auto",
    zIndex: 100,
  },
  container: {
    marginTop: 64,
  },
  isTitleFirstChild: {
    marginTop: 0,
  },
  removeMarginIfTitleFirstChild: {
    marginTop: -10,
  },
  sectionContent: {
    display: "flex",
    flexDirection: "column",
    width: "calc(100% - 290px)",
    maxWidth: "calc(100% - 290px)",
    flexGrow: 1,

    "@media print": {
      width: "100%",
      maxWidth: "100%",
    },
    [theme.breakpoints.down(940)]: {
      maxWidth: "100%",
      width: "100%",
    },
  },
  sectionContentWithoutNav: {
    maxWidth: "100%",
    width: "100%",
  },
  breadcrumbs: {
    marginBottom: 24,
    "@media print": {
      display: "none",
    },
  },
  chipContainer: {
    display: "flex",
    marginBottom: 20,
  },
  chipLink: {
    textDecoration: "none",
    marginRight: 10,
  },
  chip: {
    cursor: "pointer",
  },
  breadcrumbsLink: {
    color: "#5d5d5d",
    cursor: "pointer",
    textDecoration: "none",
    fontSize: "inherit",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  breadcrumbsActive: {
    color: "#696969",
    cursor: "default",
    textDecoration: "none",
    fontSize: "inherit",
    "&:hover": {
      textDecoration: "none",
    },
  },
  createDate: {
    marginTop: 5,
    color: "#5d5d5d",
    fontSize: ".875rem",
  },
  navigationContainer: {
    position: "absolute",
    width: 330,
    height: "calc(100% - 400px)",
    right: 0,
    display: "flex",
    justifyContent: "flex-end",
    "@media print": {
      display: "none",
    },

    [theme.breakpoints.down(1300)]: {
      width: 250,
      right: -0,
    },
    [theme.breakpoints.down(940)]: {
      display: "none",
    },
  },
  navigation: {
    position: "sticky",
    top: 150,
    height: "fit-content",
    width: 250,
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down(940)]: {
      display: "none",
    },
  },
  link: {
    color: "#f15a29",
    marginBottom: 10,
    // color: theme.color.orange,
    cursor: "pointer",
    textDecoration: "none",
    fontSize: ".825rem",
    lineHeight: 1.3,
    "&:hover": {
      textDecoration: "underline",
    },
  },
  darkLink: {
    color: "#000000",
  },
  linkBiding: {
    position: "absolute",
    top: -120,
  },
  printButton: {
    opacity: .5,
    marginLeft: 10,
    "@media print": {
      display: "none",
    },
  },
  previewImage: {
    height: "auto",
    width: "100%",
    zIndex: 2,
    transition: "display 1s ease 2s",
    backgroundColor: 'white',
  },
  previewImageContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "auto",
    width: "100%",
    marginTop: 30,
    borderRadius: 15,
    overflow: "hidden",
    willChange: 'transform',
  },
  spinnerImage: {
    color: "#f15a29",
    position: "absolute",
  },
}));

const PageWithCmsWrapper = ({
                              title,
                              content,
                              path,
                              seoProps,
                              breadcrumbTitle = "",
                              createDate = "",
                              tags = [],
                              image = "",
                              author,
                              children,
                              useSpecialLandingPageStyle,
                            }) => {
  const classes = useStyles();
  let navLinks = title ? [{
    padding: 0,
    title,
    id: title.trim(),
  }] : [];
  const [topNavigationPadding, setTopNavigationPadding] = useState(115);
  const [isMobile, setIsMobile] = useState(false);
  const [activeLink, setActiveLink] = useState();
  const [supportsSmoothScrolling, setSupportsSmoothScrolling] = useState();
  const activeLinkSafariRef = useRef(2);
  const regexFindHeader = /^#{1,6} [^!].*/gim;
  let minPadding = 7;
  const newChildrenCodeStyle = content.split("```");


  newChildrenCodeStyle.forEach((item, index) => {
    if (!(index % 2)) {
      const headersList = item.match(regexFindHeader);
      let list = [];

      if (headersList && title) {
        list = headersList.map(header => {
          const title = header.replace(/#/g, "").slice(1);
          const padding = header.length - title.length;
          (minPadding > padding) && (minPadding = padding);
          return {
            padding,
            title,
            id: title.trim(),
          };
        });
      }
      navLinks = [...navLinks, ...list];
    }
  });

  useEffect(() => {
    const window_width = document?.documentElement?.clientWidth||
      document?.body?.clientWidth || 0;
    setIsMobile(window_width < 500)

    // --------- adding smooth scrolling for click nav link ---------
    const supportsSmoothScrolling = supportsSmoothScrollingFunc();
    setSupportsSmoothScrolling(supportsSmoothScrolling);
    let anchorLinks = document && document.querySelectorAll("a[href^=\"#\"]");
    const addSmoothScroll = (e, item) => {
      let hashVal = item.getAttribute("href").slice(1).trim();
      let target = document && document.getElementById(hashVal);
      if (!target) return;
      smoothScrolling(target);
      e.preventDefault();
    };
    for (let item of anchorLinks) {
      item.addEventListener("click", e => addSmoothScroll(e, item));
    }

    // --------- calc nav widget padding (top) ---------
    const titleNode = document && document.getElementById("title");
    setTopNavigationPadding(64 + (titleNode && titleNode.offsetHeight));
    const changeScrollHeight = () => {
      setTopNavigationPadding(64 + (titleNode && titleNode.offsetHeight));
    };
    window && window.addEventListener("resize", () => changeScrollHeight());

    return () => {
      for (let item of anchorLinks) {
        item.removeEventListener("click", e => addSmoothScroll(e, item));
      }
      window && window.removeEventListener("resize", () => changeScrollHeight());
    };
  }, []);


  useEffect(() => {
    const createNav = () => {
      if (navLinks.length === 1) return;
      if (supportsSmoothScrolling) {
        const coordHeadersList = navLinks.map(item => {
          const el = document && document.getElementById(item.id);
          const itemCoord = el.getBoundingClientRect().y;
          const windowScroll = window && window.pageYOffset;
          return itemCoord + windowScroll;
        });

        const listener = () => {
          const windowScroll = window && window.pageYOffset;
          if (windowScroll < coordHeadersList[0]) return setActiveLink(0);
          if (windowScroll > coordHeadersList[coordHeadersList.length - 1]) return setActiveLink(coordHeadersList.length - 1);
          const active = coordHeadersList.findIndex(headerPosition => headerPosition > (windowScroll + 5));
          setActiveLink(active - 1);
        };

        window && window.addEventListener("scroll", listener);
        return () => {
          window && window.removeEventListener("scroll", listener);
        };
      } else {
        const coordHeadersList = navLinks.map(item => {
          const el = document && document.getElementById(item.id);
          const itemCoord = el && el.getBoundingClientRect().y;
          const windowScroll = window && window.pageYOffset;
          return itemCoord + windowScroll;
        });

        const navLinksList = document.getElementById("navLinksList");
        const listener = () => {
          const windowScroll = window && window.pageYOffset;

          if (windowScroll < coordHeadersList[0] && navLinksList) {
            if (activeLinkSafariRef.current !== 0) {
              (activeLinkSafariRef.current !== undefined) && navLinksList.childNodes[activeLinkSafariRef.current] && navLinksList.childNodes[activeLinkSafariRef.current].classList.remove("active-navigation-link");
              activeLinkSafariRef.current = 0;
              return navLinksList.childNodes[0].classList.add("active-navigation-link");
            }
          } else if (windowScroll > coordHeadersList[coordHeadersList.length - 1]) {
            if (activeLink !== (coordHeadersList.length - 1)) {
              (activeLinkSafariRef.current !== undefined) && navLinksList.childNodes[activeLinkSafariRef.current] && navLinksList.childNodes[activeLinkSafariRef.current].classList.remove("active-navigation-link");
              activeLinkSafariRef.current = coordHeadersList.length - 1;
              return navLinksList.childNodes[coordHeadersList.length - 1].classList.add("active-navigation-link");
            }
          } else {
            const activeItem = coordHeadersList.findIndex(headerPosition => headerPosition > (windowScroll + 5));
            if ((activeLinkSafariRef.current !== (activeItem - 1)) && (activeItem !== -1) && (activeItem !== 0)) {
              (activeLinkSafariRef.current !== undefined) && navLinksList.childNodes[activeLinkSafariRef.current] && navLinksList.childNodes[activeLinkSafariRef.current].classList.remove("active-navigation-link");
              activeLinkSafariRef.current = activeItem - 1;
              return navLinksList.childNodes[activeItem - 1].classList.add("active-navigation-link");
            }
          }
        };

        listener();

        window && window.addEventListener("scroll", listener);
        return () => {
          window && window.removeEventListener("scroll", listener);
        };
      }
    };

    setTimeout(createNav, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navLinks.length]);

  const goBack = () => {
    navigate(-1);
  };

  const date = createDate && new Date(createDate).toLocaleDateString("en-US", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });

  let findImageWidth;
  if (!!image) {
    const searchParams = new URLSearchParams(image.substring(image.indexOf('?')));
    const width = searchParams.get('width');
    findImageWidth = image.split("/width=");
    if (width && findImageWidth.length === 1) {
      findImageWidth = [
        ...findImageWidth,
        width
      ]
    }
  }
  return (
    <Layout
      SeoProps={seoProps}
      useSpecialLandingPageStyle={useSpecialLandingPageStyle}
      HeaderProps={{ path }}
    >
      <div id={'mainContent'}>
      <Section innerClassName={cx(classes.mainWithoutNav, { [classes.main]: navLinks.length > 1 })}
               sectionClassName={classes.mainSection}
               id={'mainContentWrapper'}
      >
        <div className={cx(classes.sectionContent, { [classes.sectionContentWithoutNav]: navLinks.length < 2 })}>


          {
            !!breadcrumbTitle &&
            <Breadcrumbs aria-label="breadcrumb" className={classes.breadcrumbs}>
              <Typography onClick={goBack} color="textPrimary"
                          className={classes.breadcrumbsLink}>{breadcrumbTitle}</Typography>
              <Typography color="textPrimary" className={classes.breadcrumbsActive}>{title}</Typography>
            </Breadcrumbs>
          }
          {
            !!tags.length &&
            <div className={classes.chipContainer}>
              {
                tags.map(({ title }) => (
                  <Link to={`/tags/${title}`} className={classes.chipLink} key={`/tags/${title}`}>
                    <Chip label={title} color={"primary"} className={classes.chip} />
                  </Link>
                ))
              }
            </div>
          }
          {
            title &&
            <Typography variant="h2" id={"title"}>
              {title}
              <div id={title.trim()} className={classes.linkBiding} />
              <IconButton color="text" aria-label="print" onClick={() => {
                try {
                  document.execCommand("print", false, null);
                } catch (e) {
                  window.print();
                }
              }} className={classes.printButton}>
                <PrintIcon />
              </IconButton>
            </Typography>
          }
          {
            !!date &&
            <Typography className={classes.createDate}>
              {date + (!!author && !!author.username && author.username ? `  —  ${author.username}` : "")}
            </Typography>
          }
          {
            !!image && <div className={classes.previewImageContainer} style={{
              marginTop: useSpecialLandingPageStyle && 0,
              marginBottom: useSpecialLandingPageStyle && 30,
            }}>
              <CircularProgress className={classes.spinnerImage} />
              <img src={findImageWidth[0]}
                   alt="Preview"
                   className={classes.previewImage}
                   style={{ maxWidth: isMobile ? "100%" : findImageWidth[1] }}
              />
            </div>
          }
          <div className={cx(classes.markdownWrapperContainer,{
            [classes.isTitleFirstChild]: content.trim().startsWith("#"),
            [classes.removeMarginIfTitleFirstChild]: content.trim().startsWith("#") && useSpecialLandingPageStyle,
            [classes.container]: !useSpecialLandingPageStyle,
          })}>
            <MarkdownWrapper children={content}
                             title={title}
                             newChildrenCodeStyle={newChildrenCodeStyle}
            />
          </div>
        </div>
        {
          navLinks && navLinks.length > 1 &&
          <div className={classes.navigationContainer} style={{ top: topNavigationPadding }}>
            <div className={classes.navigation} id={"navLinksList"}>
              {
                navLinks.map(({ padding, title, id }, index) => (
                  <a key={id}
                     style={{ paddingLeft: (padding - minPadding) * 5 + "px" }}
                     className={cx(classes.link,
                       { [classes.darkLink]: supportsSmoothScrolling && (activeLink === index) },
                     )}
                     href={`#${id}`}
                  >
                    {title}
                  </a>
                ))
              }
            </div>
          </div>
        }
        {children}

      </Section>
      </div>
    </Layout>
  );
};

export default PageWithCmsWrapper;
